import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, Edit2, Check, X, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserProfileMenu from './UserProfileMenu';
import ModelUpload from './ModelUpload';
import AK47MagDump from './AK47MagDump';

const UnifiedHeader = ({ 
  searchTerm = '', 
  onSearchChange = null, 
  showSearch = true,
  showUpload = true,
  showAK47 = true,
  onUploadSuccess = null,
  onAK47Complete = null,
  // Code editor specific props
  modelName = null,
  currentSession = null,
  renamingSession = false,
  sessionRenameValue = '',
  setSessionRenameValue = null,
  onBackToModel = null,
  onStartSessionRename = null,
  onSaveSessionRename = null,
  onCancelSessionRename = null,
  isLayerModified = false
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAK47Modal, setShowAK47Modal] = useState(false);

  const handleUploadClick = () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    setShowUploadModal(true);
  };

  const handleUploadSuccess = (result) => {
    console.log('Upload successful:', result);
    setShowUploadModal(false);
    
    // Call custom callback if provided (for ModelsLanding refresh)
    if (onUploadSuccess) {
      onUploadSuccess(result);
    } else {
      // Default behavior: navigate to the uploaded model
      navigate(`/models/${result.model_id}`);
    }
  };

  const handleAK47Complete = (results) => {
    console.log('AK47 mag dump completed:', results);
    setShowAK47Modal(false);
    
    // Call custom callback if provided (for ModelsLanding refresh)
    if (onAK47Complete) {
      onAK47Complete(results);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="bg-[#0b0f19] border-b border-[#1f2937]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Left side: Logo, Model Name, Session */}
            <div className="flex items-center space-x-4 flex-1">
              {/* Logo */}
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-3 hover:opacity-80 transition-opacity flex-shrink-0"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                <div className="flex items-center justify-center w-10 h-10 bg-orange-500 rounded-lg">
                  <span className="text-xl font-bold text-white">F</span>
                </div>
                <div className="text-2xl font-semibold text-white">ForgeML</div>
              </button>

              {/* Model Navigation - when in code editor */}
              {modelName && onBackToModel && (
                <>
                  <div className="h-6 w-px bg-[#1f2937]"></div>
                  <button
                    onClick={onBackToModel}
                    className="flex items-center space-x-2 text-orange-500 hover:text-orange-400 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="font-medium">{modelName}</span>
                  </button>
                </>
              )}

              {/* Session Information */}
              {currentSession && (
                <>
                  <div className="h-6 w-px bg-[#1f2937]"></div>
                  {renamingSession ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-[#888888]">Session:</span>
                      <input
                        type="text"
                        value={sessionRenameValue}
                        onChange={(e) => setSessionRenameValue && setSessionRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onSaveSessionRename && onSaveSessionRename();
                          } else if (e.key === 'Escape') {
                            onCancelSessionRename && onCancelSessionRename();
                          }
                        }}
                        className="px-2 py-1 text-sm border border-[#374151] bg-[#1f2937] text-white rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Session name..."
                        autoFocus
                      />
                      <button
                        onClick={onSaveSessionRename}
                        className="p-1 text-green-600 hover:text-green-700"
                        title="Save"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={onCancelSessionRename}
                        className="p-1 text-gray-600 hover:text-gray-700"
                        title="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-[#888888]">Session:</span>
                      <span className="text-sm font-medium text-white">
                        {currentSession.session_name || `Session ${currentSession.id.slice(0, 8)}`}
                      </span>
                      <button
                        onClick={onStartSessionRename}
                        className="p-1 text-[#666666] hover:text-white transition-colors"
                        title="Rename session"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Autosave indicator */}
              {isLayerModified && (
                <>
                  <div className="h-6 w-px bg-[#1f2937]"></div>
                  <div className="flex items-center space-x-2 text-sm text-green-500">
                    <Save className="h-4 w-4" />
                    <span>Changes autosaved</span>
                  </div>
                </>
              )}

              {/* Search Bar - when not in code editor */}
              {showSearch && (
                <>
                  <div className="h-6 w-px bg-[#1f2937]"></div>
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-5 w-5" />
                      <input
                        type="text"
                        placeholder="Search models, tasks, tags..."
                        value={searchTerm}
                        onChange={onSearchChange ? (e) => onSearchChange(e.target.value) : undefined}
                        className="pl-10 pr-4 py-2 w-full border border-[#374151] bg-[#1f2937] text-white placeholder-gray-500 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        readOnly={!onSearchChange}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right side: Authentication UI */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              {isAuthenticated ? (
                <>
                  {showUpload && (
                    <button
                      onClick={handleUploadClick}
                      className="bg-orange-500 text-white hover:bg-orange-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Upload
                    </button>
                  )}
                  {showAK47 && (
                    <button
                      onClick={() => setShowAK47Modal(true)}
                      className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      AK47
                    </button>
                  )}
                  <UserProfileMenu />
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/signin')}
                    className="text-[#888888] hover:text-white px-3 py-2 text-sm font-medium transition-colors"
                  >
                    Sign in
                  </button>
                  {showUpload && (
                    <button
                      onClick={handleUploadClick}
                      className="bg-orange-500 text-white hover:bg-orange-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Upload
                    </button>
                  )}
                  {showAK47 && (
                    <button
                      onClick={() => setShowAK47Modal(true)}
                      className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      AK47
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <ModelUpload
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* AK47 Modal */}
      {showAK47Modal && (
        <AK47MagDump
          onClose={() => setShowAK47Modal(false)}
          onComplete={handleAK47Complete}
        />
      )}
    </>
  );
};

export default UnifiedHeader;